<template>
  <ContentSection>
    <div class="relative rounded-bl-3xl bg-red-500 px-14 py-12 text-white">
      <Form
        v-if="frontendVotingForm && state !== 'success' && state !== 'error' && !votingDeadlineOver"
        key="form"
        ref="form"
        v-slot="{ errors }"
        novalidate
        @submit="onSubmit"
      >
        <div
          v-for="(step, index) in frontendVotingForm.sections"
          v-show="activeStep === index"
          :key="index"
          class="
            grid grid-cols-4 gap-3
            sm:grid-cols-12 sm:gap-5
          "
        >
          <template
            v-for="inputField in step.fields"
            :key="inputField.handle"
          >
            <FormTitle
              v-if="inputField.handle.startsWith('form_title_')"
              :field="inputField"
              heading-class="typo-h-2"
            />
            <!-- TEXT -->
            <InputField
              v-else-if="inputField.type === 'text' || inputField.type === 'textarea'"
              :field="inputField"
              :rules="frontendVotingForm.rules[inputField.handle]"
              :error="errors[inputField.handle]"
              class="slide-left text-black"
              container-background="colored"
            />
            <!-- RADIO GROUP -->
            <RadioGroup
              v-else-if="inputField.type === 'radio'"

              :field="inputField"
              :rules="frontendVotingForm.rules[inputField.handle]"
              :error="errors[inputField.handle]"
              :options="inputField.config.options"
              :show-label="false"
              class="slide-left"
              container-background="colored"
              :options-highlighted="true"
            />
          </template>
        </div>
        <!-- CHECKBOX ACCEPT CONDITIONS -->
        <CheckboxGroup
          v-if="textCheckboxConditions && activeStep === frontendVotingForm.sections.length - 1"
          key="accept_conditions"
          :field="{
            handle: 'accept_conditions',
            width: 100,
          }"
          class="slide-left mt-8"
          label="Bestimmungen"
          rules="required"
          :error="errors.accept_conditions"
          :options="{
            accept_conditions: textCheckboxConditions,
          }"
          container-background="colored"
        />
        <!-- HONEYPOT -->
        <Field :name="frontendVotingForm.honeypot">
          <input class="hidden" :name="frontendVotingForm.honeypot" />
        </Field>
        <div class="col-span-12 mt-17 flex justify-between">
          <div>
            <span>{{ t('voting.deadline_text') }}&nbsp;</span>
            <NuxtTime
              :datetime="data.voting_deadline"
              weekday="long"
              month="long"
              day="numeric"
              year="numeric"
              :locale="localeObj?.language"
            />
          </div>
          <template v-for="(step, index) in frontendVotingForm.sections" :key="index">
            <AppButton
              v-if="index === frontendVotingForm.sections.length - 2"
              v-show="activeStep === index"
              type="button"
              class="shrink-0"
              variant="secondary"
              :disabled="!form?.values?.voting_options"
              @click="activeStep = activeStep + 1"
            >
              {{ toVoteButtonText }}
              <template #icon>
                <IconArrowRight class="h-3" />
              </template>
            </AppButton>
            <AppButton
              v-if="index === frontendVotingForm.sections.length - 1"
              v-show="activeStep === index"
              type="submit"
              class="shrink-0"
              variant="secondary"
            >
              {{ castVoteButtonText }}
              <template #icon>
                <IconArrowRight class="h-3" />
              </template>
            </AppButton>
          </template>
        </div>

        <slot :errors="errors" />
      </Form>

      <!-- SUCCESS SCREEN -->
      <VotingInfoPanel
        v-if="state === 'success'"
        :title="data.final_screen_title"
        :text="data.final_screen_text"
      >
        <template #icon>
          <IconCheck class="h-12" />
        </template>
        <VotingResultBars
          v-if="frontendVotingForm && data.show_intermediate_results"
          :form-handle="frontendVotingForm.handle"
          :translated-form-handles="translatedFormHandles"
        />
        <template v-if="data.show_intermediate_results" #footer>
          {{ t('voting.results_might_change_until_deadline', {
            deadline: formatDate(data.voting_deadline),
          }) }}
        </template>
        <div
          v-if="!data.show_intermediate_results"
          class="
            mt-8 flex w-max items-center gap-3 rounded-bl-2xl bg-white/10 p-4
          "
        >
          <IconInfo class="h-6" />
          {{ t('voting.you_voted_for', { option: selectedVotingOptionValue || '' }) }}
        </div>
      </VotingInfoPanel>
      <!-- VOTING DEADLINE OVER -->
      <VotingInfoPanel
        v-if="votingDeadlineOver"
        :title="data.voting_over_screen_title"
        :text="data.voting_over_screen_text"
      >
        <VotingResultBars
          v-if="frontendVotingForm && data.show_results"
          :form-handle="frontendVotingForm.handle"
          :translated-form-handles="translatedFormHandles"
        />
      </VotingInfoPanel>
      <!-- ERROR SCREEN -->
      <VotingInfoPanel
        v-if="state === 'error'"
        :title="t('voting.error_screen_title')"
        :text="t('voting.error_screen_text')"
      >
        <ul class="typo-h-5 mt-2 text-white">
          <li v-for="(errorMessage, index) in errorMessages" :key="index">
            {{ t(`voting.${errorMessage}`) }}
          </li>
          <li v-if="Object.keys(errorMessages).length === 0">
            {{ t('voting.error_default_text') }}
          </li>
        </ul>
      </VotingInfoPanel>
    </div>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsVotingFragment } from '#gql'
import {
  Form, Field,
} from 'vee-validate'

const props = defineProps<{ data: Enforce<MainComponentsVotingFragment> }>()

type FormFields = { [key: string]: string | Array<string> | undefined }

type Field = {
  handle: string
  value: string | Array<string>
}
const {
  t, locale, locales,
} = useI18n()
const localeObj = locales.value.find(site => site.code === locale.value)

const config = useRuntimeConfig()
const state = ref('idle')
const form = ref<InstanceType<typeof Form> | null>(null)
const activeStep = ref(0)
const errorMessages = ref<Record<string, string>>({})

const frontendVotingForm = computed(() => {
  const selectedForm = props.data.voting_forms.find(form => form.visible_in_frontend)

  if (!selectedForm?.voting_form?.sections) {
    return undefined
  }

  // Create a shallow copy to avoid mutating the original data
  const filteredForm = {
    ...selectedForm.voting_form,
    sections: selectedForm.voting_form.sections.filter(section => section.display !== 'settings'),
  }

  return filteredForm
})

const translatedFormHandles = computed(() => {
  return props.data.voting_forms.map(form => form.voting_form.handle).filter(handle => handle !== frontendVotingForm.value?.handle)
})

const castVoteButtonText = computed(() => {
  return props.data.cast_vote_button_text || 'Stimme abgeben'
})

const toVoteButtonText = computed(() => {
  return props.data.to_vote_button_text || 'Jetzt abstimmen'
})

const textCheckboxConditions = computed(() => {
  if (!props.data.show_checkbox_conditions) {
    return ''
  }

  return props.data.text_checkbox_conditions
})

const selectedVotingOptionValue = ref('')

function setSelectedVotingOptionValue() {
  const selectedKey = form.value?.values?.voting_options

  if (!selectedKey || !frontendVotingForm.value) return null

  for (const section of frontendVotingForm.value.sections) {
    for (const field of section.fields) {
      if (field.handle === 'voting_options' && Array.isArray(field.config?.options)) {
        const option = field.config.options.find((opt: any) => opt.key === selectedKey)
        selectedVotingOptionValue.value = option?.value || null
      }
    }
  }
}

const votingDeadlineOver = computed(() => {
  return new Date(props.data.voting_deadline) < new Date()
})

function formatDate(date: string) {
  if (!date) return ''

  const dateObject = new Date(date)
  if (!dateObject) return ''

  return dateObject.toLocaleDateString(localeObj?.language || 'de-CH', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function initFormData(fields: FormFields) {
  // forget undefined fields
  fields = Object.keys(fields).reduce<Record<string, string | Array<string>>>((acc, key) => {
    if (fields[key] !== undefined) {
      acc[key] = fields[key]
    }
    return acc
  }, {})

  // convert fields to array
  const outputFields: Field[] = Object.keys(fields)
    .map(key => ({
      handle: key,
      value: fields[key],
    }))
    .filter(field => field.value !== undefined) as Field[]

  const formData = new FormData()

  outputFields.forEach((field) => {
    if (field.value instanceof Array) {
      // This will handle non-file arrays
      field.value.forEach((item) => {
        formData.append(`${field.handle}[]`, item)
      })
    } else {
      formData.append(field.handle, field.value as string)
    }
  })

  return formData
}

async function onSubmit(fields: FormFields) {
  state.value = 'pending'
  setSelectedVotingOptionValue()

  if (!frontendVotingForm.value) return

  try {
    await $fetch(
      `${config.public.BACKEND_URL}/!/forms/${frontendVotingForm.value.handle}`,
      {
        method: 'POST',
        body: initFormData(fields),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      },
    )

    state.value = 'success'

    // form.value?.resetForm()
  } catch (error) {
    state.value = 'error'
    if (error.response) {
      // When response status is not OK
      const errors = error.response._data?.errors
      errorMessages.value = errors
      console.error('Form Submit', errors)
    } else {
      // When there is no response or other error occurs
      console.error('Error:', error.message || error)
    }
  }
}
</script>
